/**
 * Change 7 — Attendance Report Dashboard
 * Uses GET /api/hr/attendance/daily-summary for live data.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { apiGet } from '../../lib/api';
import { RefreshCw, Printer, BarChart3, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import PageHeader from '../../components/PageHeader';

function KpiTile({ label, value, color = 'text-slate-700', bg = 'bg-white' }) {
  return (
    <div className={`rounded-sm p-4 border border-slate-200 ${bg}`} data-testid={`att-kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-400">{label}</div>
      <div className={`text-2xl font-display font-extrabold mt-1 ${color}`}>{value}</div>
    </div>
  );
}

export default function AttendanceReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiGet('/hr/attendance/daily-summary', { month });
      setData(r);
    } catch { toast.error('Failed to load attendance summary'); }
    finally { setLoading(false); }
  }, [month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const tiles = data ? [
    { label: 'Total Staff', value: data.total, color: 'text-slate-700', bg: 'bg-slate-50' },
    { label: 'Present Today', value: data.present, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Absent', value: data.absent, color: 'text-red-600', bg: 'bg-red-50' },
    { label: 'Half Day', value: data.half_day, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'On Leave', value: data.on_leave, color: 'text-violet-600', bg: 'bg-violet-50' },
    { label: 'WFH', value: data.wfh, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Not Marked', value: data.not_marked, color: 'text-slate-400', bg: 'bg-slate-50' },
    { label: 'Attendance %', value: `${data.attendance_pct}%`, color: 'text-[#3b4872]', bg: 'bg-indigo-50' },
  ] : [];

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <PageHeader
          overline="People & HR"
          title="Attendance Report"
          subtitle={`Live daily summary · ${data?.date || '…'}`}
          icon={<BarChart3 size={18} />}
        />
        <div className="flex items-center gap-2 no-print">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="border border-slate-300 rounded-sm px-3 py-1.5 text-sm"
            data-testid="att-report-month"
          />
          <button
            onClick={fetchData}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 rounded-sm text-xs font-display font-bold hover:bg-slate-50 disabled:opacity-40"
            data-testid="att-report-refresh"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#3b4872] text-white rounded-sm text-xs font-display font-bold hover:bg-[#2d3856]"
            data-testid="att-report-pdf"
          >
            <Printer size={13} /> Print / PDF
          </button>
        </div>
      </div>

      {loading && !data && (
        <div className="text-center py-16 text-slate-400">
          <Loader2 size={20} className="animate-spin inline mr-2" />Loading…
        </div>
      )}

      {data && (
        <>
          {/* KPI Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="att-report-kpis" id="att-report-printable">
            {tiles.map(t => (
              <KpiTile key={t.label} {...t} />
            ))}
          </div>

          {/* Department Breakdown */}
          {data.department_breakdown?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="att-report-dept-table">
              <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                <h3 className="font-display font-bold text-sm">Department-wise Breakdown · {data.date}</h3>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-[#3b4872] text-white text-[10px] uppercase tracking-widest font-display">
                  <tr>
                    <th className="text-left px-3 py-2">Department</th>
                    <th className="text-right px-3 py-2">Total</th>
                    <th className="text-right px-3 py-2 text-emerald-300">Present</th>
                    <th className="text-right px-3 py-2 text-red-300">Absent</th>
                    <th className="text-right px-3 py-2 text-violet-300">Leave</th>
                    <th className="text-right px-3 py-2 text-slate-300">Not Marked</th>
                    <th className="text-right px-3 py-2">Att %</th>
                  </tr>
                </thead>
                <tbody>
                  {data.department_breakdown.map(d => (
                    <tr key={d.department} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-2 font-display font-semibold">{d.department}</td>
                      <td className="px-3 py-2 text-right">{d.total}</td>
                      <td className="px-3 py-2 text-right text-emerald-600 font-bold">{d.present}</td>
                      <td className="px-3 py-2 text-right text-red-600 font-bold">{d.absent}</td>
                      <td className="px-3 py-2 text-right text-violet-600 font-bold">{d.leave}</td>
                      <td className="px-3 py-2 text-right text-slate-400">{d.not_marked}</td>
                      <td className="px-3 py-2 text-right font-bold">
                        {d.total > 0 ? `${Math.round(d.present / d.total * 100)}%` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
